import * as XLSX from "xlsx";
import { findColumnIndex, detectEventColumns, normalizeString, extractEvents, parseNumber } from "./excelUtils.js";
import { REQUIRED_COLUMNS, WAVELENGTHS } from "./constants.js";

/**
 * Parse la sheet Atténuations et extrait les fibres avec leurs moyennes et événements
 * @param {Object} worksheet - Sheet Excel à parser
 * @param {string} sheetName - Nom de la sheet
 * @returns {Object} {success: boolean, fibers: Array, error: Object}
 *
 * Structure :
 * - Ligne 1 (index 0): Headers
 * - Lignes 2-4 (index 1-3): Moyennes/sous-headers
 * - Ligne 5+ (index 4+): Données fibres
 */
export function parseSheet(worksheet, sheetName) {
  const rawData = XLSX.utils.sheet_to_json(worksheet, {
    header: 1,
    defval: "",
  });

  console.log(`[${sheetName}] Nombre total de lignes:`, rawData.length);

  if (rawData.length < 5) {
    return {
      success: false,
      error: {
        code: "NO_DATA",
        message: `La feuille "${sheetName}" ne contient pas assez de données`,
        details: `Minimum 5 lignes requis (header + moyennes + données), ${rawData.length} trouvées`,
      },
    };
  }

  const headerRow = rawData[0];
  const dataRows = rawData.slice(4); // Ligne 5+ (index 4+)

  // Validation des colonnes requises
  const requiredColumns = [
    REQUIRED_COLUMNS.ID_CABLE,
    REQUIRED_COLUMNS.NUM_FIBRE,
    REQUIRED_COLUMNS.COULEUR,
    REQUIRED_COLUMNS.LAMBDA,
    REQUIRED_COLUMNS.MOYENNE_CONNECTEURS,
    REQUIRED_COLUMNS.MOYENNE_EPISSURES,
    REQUIRED_COLUMNS.PERTE_TOTALE,
  ];

  const columnMap = {};
  const missingColumns = [];

  requiredColumns.forEach((colName) => {
    const index = findColumnIndex(headerRow, colName);
    if (index !== -1) {
      columnMap[colName] = index;
    } else {
      missingColumns.push(colName);
    }
  });

  if (missingColumns.length > 0) {
    return {
      success: false,
      error: {
        code: "MISSING_COLUMNS",
        message: `Colonnes obligatoires manquantes dans "${sheetName}"`,
        details: `Colonnes manquantes: ${missingColumns.join(", ")}`,
      },
    };
  }

  // Détection des colonnes événements
  const eventColumns = detectEventColumns(headerRow);

  if (eventColumns.length === 0) {
    return {
      success: false,
      error: {
        code: "NO_EVENT_COLUMNS",
        message: `Aucune colonne événement trouvée dans "${sheetName}"`,
        details: "Les colonnes événements doivent être nommées 1, 2, 3, 4...",
      },
    };
  }

  // Parser chaque ligne de données
  const fibers = [];
  let validRows = 0;
  let skippedRows = 0;

  dataRows.forEach((row, rowIndex) => {
    try {
      const fiber = parseFiberRow(row, columnMap, eventColumns, rowIndex + 5);

      if (fiber) {
        fibers.push(fiber);
        validRows++;
      } else {
        skippedRows++;
      }
    } catch (error) {
      skippedRows++;
      console.warn(`Erreur parsing ligne ${rowIndex + 5}:`, error.message);
    }
  });

  return {
    success: true,
    fibers,
    stats: {
      totalRows: dataRows.length,
      validRows,
      skippedRows,
    },
  };
}

/**
 * Parse une ligne de fibre et extrait ses données
 * @param {Array} row - Ligne de données
 * @param {Object} columnMap - Mapping des colonnes
 * @param {Array} eventColumns - Colonnes événements
 * @param {number} originalRowIndex - Index de la ligne dans le fichier original
 * @returns {Object|null} Données de la fibre, ou null si invalide
 */
function parseFiberRow(row, columnMap, eventColumns, originalRowIndex) {
  // Extraction des colonnes de base
  const idCable = normalizeString(row[columnMap[REQUIRED_COLUMNS.ID_CABLE]]);
  const numFibre = normalizeString(row[columnMap[REQUIRED_COLUMNS.NUM_FIBRE]]);
  const couleur = normalizeString(row[columnMap[REQUIRED_COLUMNS.COULEUR]]);
  const lambda = normalizeString(row[columnMap[REQUIRED_COLUMNS.LAMBDA]]);

  // Validation des données obligatoires
  if (!idCable || !numFibre || !lambda) {
    return null;
  }

  // Validation du lambda (doit être 1310 ou 1550)
  if (!Object.values(WAVELENGTHS).slice(0, 2).includes(lambda)) {
    return null;
  }

  // Extraction des 3 moyennes
  const moyenneConnecteurs = parseNumber(row[columnMap[REQUIRED_COLUMNS.MOYENNE_CONNECTEURS]]);
  const moyenneEpissures = parseNumber(row[columnMap[REQUIRED_COLUMNS.MOYENNE_EPISSURES]]);
  const perteTotale = parseNumber(row[columnMap[REQUIRED_COLUMNS.PERTE_TOTALE]]);

  // Valider que les 3 moyennes sont présentes
  if (moyenneConnecteurs === null || moyenneEpissures === null || perteTotale === null) {
    return null;
  }

  // Extraction des événements (pour l'affichage)
  const events = extractEvents(row, eventColumns);

  // Une fibre sans événements n'est pas valide
  if (events.length === 0) {
    return null;
  }

  // Construction de l'objet fibre
  return {
    idCable,
    numFibre,
    couleur,
    lambda,
    eventCount: events.length,
    attenuations: events,
    // Moyennes utilisées pour la comparaison
    moyenneConnecteurs,
    moyenneEpissures,
    perteTotale,
    originalRowIndex,
  };
}
